import AdSlot from "../../../components/base/AdSlot";
import type { QuizQuestion as QuizQuestionType } from "../../../mocks/quizzes";

interface Props {
  question: QuizQuestionType;
  questionIndex: number;
  totalQuestions: number;
  onAnswer: (value: string) => void;
}

export default function QuizQuestion({ question, questionIndex, totalQuestions, onAnswer }: Props) {
  const progress = (questionIndex / totalQuestions) * 100;
  const hasImages = question.options.some((o) => o.image);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center px-4 py-16">
      <div className="w-full max-w-3xl">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
              Question {questionIndex + 1} of {totalQuestions}
            </span>
            <span className="text-xs font-bold text-[#FF6B6B]">
              {Math.round(progress)}% complete
            </span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* ── Ad: Leaderboard above question (every other question) ── */}
        {questionIndex % 2 === 0 && (
          <div className="mb-6 flex justify-center">
            <div className="hidden md:block">
              <AdSlot size="leaderboard" />
            </div>
            <div className="md:hidden w-full flex justify-center">
              <AdSlot size="mobile-banner" />
            </div>
          </div>
        )}

        {/* Question */}
        <div className="bg-white rounded-3xl p-8 mb-6 text-center">
          <h2
            className="text-2xl md:text-3xl font-extrabold text-gray-900"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            {question.text}
          </h2>
        </div>

        {/* Options */}
        {hasImages ? (
          <div className="grid grid-cols-2 gap-4">
            {question.options.map((opt) => (
              <button
                key={opt.id}
                onClick={() => onAnswer(opt.value)}
                className="group relative bg-white rounded-2xl overflow-hidden border-2 border-transparent hover:border-[#FF6B6B] transition-all cursor-pointer text-left hover:scale-[1.02] active:scale-95"
              >
                <div className="w-full h-40">
                  <img
                    src={opt.image}
                    alt={opt.text}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-3 flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full border-2 border-gray-200 group-hover:border-[#FF6B6B] group-hover:bg-[#FF6B6B] transition-colors flex-shrink-0">
                    <i className="ri-check-line text-white text-xs opacity-0 group-hover:opacity-100" />
                  </div>
                  <span className="text-sm font-semibold text-gray-700 group-hover:text-[#FF6B6B] transition-colors leading-tight">
                    {opt.text}
                  </span>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {question.options.map((opt) => (
              <button
                key={opt.id}
                onClick={() => onAnswer(opt.value)}
                className="group bg-white border-2 border-gray-100 rounded-2xl px-6 py-4 text-left hover:border-[#FF6B6B] hover:bg-orange-50 transition-all cursor-pointer active:scale-95 flex items-center gap-3"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-gray-200 group-hover:border-[#FF6B6B] group-hover:bg-[#FF6B6B] transition-colors flex-shrink-0">
                  <i className="ri-check-line text-white text-sm opacity-0 group-hover:opacity-100" />
                </div>
                <span className="font-semibold text-gray-700 group-hover:text-[#FF6B6B] transition-colors text-sm leading-tight">
                  {opt.text}
                </span>
              </button>
            ))}
          </div>
        )}

        <p className="text-center text-xs text-gray-400 mt-6">
          Tap any option to continue · No going back, so choose wisely! 😄
        </p>

        {/* ── Ad: Rectangle below options (every other question) ── */}
        {questionIndex % 2 === 1 && (
          <div className="mt-8 flex justify-center">
            <div className="hidden md:block">
              <AdSlot size="large-rectangle" />
            </div>
            <div className="md:hidden w-full flex justify-center">
              <AdSlot size="large-rectangle" />
            </div>
          </div>
        )}
      </div>

      {/* ── Ad: Sticky bottom banner (mobile only) ── */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 flex items-center justify-center py-1">
        <AdSlot size="sticky-mobile" />
      </div>
    </div>
  );
}
